import Button from '@ausbom/button/lib/Button';
import { Alert } from '@ausbom/form';
import type { Status } from '@ausbom/form/lib/types';
import { clsx } from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Close24 } from '../Icons';
import './CustomAlert.scss';

type CustomAlertProps = {
  status: Status;
  children: React.ReactNode;
  autoFocus?: boolean;
  className?: string;
  dismissible?: boolean;
  instantlyDismiss?: boolean;
  global?: boolean;
  onDismiss?: () => void;
  role?: React.AriaRole;
  'aria-label'?: string;
  'data-testid'?: string;
};

const CustomAlert: React.FC<CustomAlertProps> = ({
  status,
  children,
  autoFocus = false,
  className = '',
  dismissible = false,
  instantlyDismiss = true,
  global = false,
  onDismiss,
  role = 'alert',
  'aria-label': ariaLabel,
  'data-testid': dataTestId
}) => {
  const alertRef = useRef<HTMLDivElement>(null);
  const [isShown, setIsShown] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
    onDismiss && onDismiss();

    if (!instantlyDismiss) {
      setTimeout(() => setIsShown(false), 750); // Allow transitions
    } else {
      setIsShown(false);
    }
  }, [instantlyDismiss, onDismiss]);

  // Allow alerts to take focus when requested so screen readers announce the message.
  useEffect(() => {
    if (!autoFocus) return;

    const el = alertRef.current;
    if (!el) return;

    // small delay ensures screen reader picks it up correctly
    const id = setTimeout(() => {
      el.focus();
    }, 100);

    return () => clearTimeout(id);
  }, [autoFocus]);

  const isError = status === 'error';

  // Don't render if dismissed
  if (!isShown) {
    return null;
  }

  return (
    <div
      className={clsx('wdh-alert', {
        'wdh-alert--open': !isDismissed,
        'wdh-alert--closed': isDismissed,
        'wdh-alert--global': global
      })}
    >
      {/* THIS is the element that gets focus */}
      <div
        className={clsx(
          'custom-alert-container',
          {
            'error-alert-container': isError,
            'dismissible-alert': dismissible
          },
          className
        )}
        ref={alertRef}
        tabIndex={-1} // always focusable
        role={role} // ensures announcement if 'alert'
        aria-label={ariaLabel}
        data-testid={dataTestId}
        aria-atomic={isError ? 'true' : undefined}
      >
        <Alert status={status}>
          <div className="alert-content-container">
            <div className="alert-message">{children}</div>

            {dismissible && (
              <Button className="dismiss-button" onClick={handleDismiss} aria-label="Dismiss alert">
                <Close24 />
              </Button>
            )}
          </div>
        </Alert>
      </div>
    </div>
  );
};

export default CustomAlert;
